import { useState } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { ActivityIndicator } from "@components/ActivityIndicator";
import { Button } from "@components/Button";
import { ErrorComponent } from "@components/ErrorComponent";
import { Loader } from "@components/Loader";
import { MainLayout } from "@components/MainLayout";
import { Spacing, SpacingSizes } from "@components/Spacing";
import { Body, H2 } from "@components/Typography";
import { Routes } from "@ctypes";
import {
  useBeneficiaryGiftingLink,
  useCheckGiftingCodeAvailability,
  useConfirmBeneficiaryGiftingCode,
  useDebounce,
  useErrorBanner,
  useTheme,
  useUpdateBeneficiaryGiftingCode,
  useUser,
} from "@hooks";
import { i18n, Translations } from "@i18n";
import { useStyles } from "./styles";
import { RequestGiftRouteComponentProps, RequestGiftRouteProps, RequestGiftState } from "./types";

const MAX_CODE_LENGTH = 10;

export const RequestGiftRoute = () => {
  const location = useLocation();

  const beneficiaryId = (location.state as RequestGiftRouteProps)?.beneficiaryId;

  const { data, loading, error, refetch } = useBeneficiaryGiftingLink({
    beneficiaryId,
  });

  if (!beneficiaryId) return <Navigate to={Routes.Accounts} replace />;

  if (loading) {
    return (
      <MainLayout pageHeader={i18n.t(Translations.RequestGiftRouteHeader)}>
        <Loader />
      </MainLayout>
    );
  }

  if (error || !data?.giftingUrl || !data?.giftingCode) {
    return (
      <MainLayout pageHeader={i18n.t(Translations.RequestGiftRouteHeader)}>
        <ErrorComponent />
      </MainLayout>
    );
  }

  return <RequestGiftRouteComponent beneficiaryId={beneficiaryId} data={data} refetch={refetch} />;
};

const RequestGiftRouteComponent = ({
  beneficiaryId,
  data,
  refetch,
}: RequestGiftRouteComponentProps) => {
  const styles = useStyles();
  const { theme } = useTheme();
  const { showErrorBanner } = useErrorBanner();

  const [screenState, setScreenState] = useState<RequestGiftState>(
    data.isGiftingCodeConfirmed ? RequestGiftState.Confirmed : RequestGiftState.Initial,
  );
  const [code, setCode] = useState(data.giftingCode ?? "");
  const [copied, setCopied] = useState(false);

  const { beneficiaries } = useUser();
  const beneficiaryName = beneficiaries?.find(
    (beneficiary) => beneficiary?.id === beneficiaryId,
  )?.firstName;

  const [checkGiftingCodeAvailability, { data: availability, loading: isLoadingAvailability }] =
    useCheckGiftingCodeAvailability();

  const debouncedCheckGiftingCodeAvailability = useDebounce(checkGiftingCodeAvailability);

  const [confirmBeneficiaryGiftingCode, { loading: isLoadingConfirm }] =
    useConfirmBeneficiaryGiftingCode();
  const [updateBeneficiaryGiftingCode, { loading: isLoadingUpdate }] =
    useUpdateBeneficiaryGiftingCode();

  const isPending = isLoadingConfirm || isLoadingUpdate;
  const isConfirmed = screenState === RequestGiftState.Confirmed;

  const baseUrl = data.giftingUrl
    .replace(/^https?:\/\//, "")
    .replace(new RegExp(`${data.giftingCode}$`), "");

  const fullGiftUrl = data.isGiftingCodeConfirmed ? data.giftingUrl : `https://${baseUrl}${code}`;

  const hasUrlChanges = code !== data.giftingCode;

  const disabled = Boolean(
    hasUrlChanges &&
    (isLoadingAvailability || !availability?.checkGiftingCodeAvailability?.isAvailable),
  );

  const primaryCTALabel = (() => {
    if (isConfirmed) return i18n.t(Translations.RequestGiftRouteShareCTA);
    if (hasUrlChanges) return i18n.t(Translations.RequestGiftRouteEditCTA);
    return i18n.t(Translations.RequestGiftRouteConfirmCTA);
  })();

  const handleCopy = () => {
    navigator.clipboard.writeText(fullGiftUrl);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  const handleSubmit = async () => {
    try {
      if (!isConfirmed) {
        if (hasUrlChanges) {
          await updateBeneficiaryGiftingCode({
            variables: { beneficiaryId, newGiftingCode: code },
          });
        } else {
          await confirmBeneficiaryGiftingCode({
            variables: { beneficiaryId },
          });
        }

        await refetch();
        setScreenState(RequestGiftState.Confirmed);
        return;
      }

      const message = i18n.t(Translations.RequestGiftRouteShareMessage, {
        name: beneficiaryName ?? "",
        link: fullGiftUrl,
      });

      if (navigator.share) {
        try {
          await navigator.share({ text: message });
          return;
        } catch {
          // Dismissed or unsupported payload – fall back to copying.
        }
      }

      handleCopy();
    } catch (e: any) {
      const message = e.graphQLErrors?.[0]?.message;
      showErrorBanner(message);
    }
  };

  const onChangeCode = (newCode: string) => {
    const normalizedCode = newCode.toLowerCase();

    setCode(normalizedCode);

    if (newCode.length <= MAX_CODE_LENGTH || newCode.length === 0) {
      debouncedCheckGiftingCodeAvailability({
        variables: { giftingCode: normalizedCode, beneficiaryId },
      });
    }
  };

  const validationMessage = (() => {
    if (isLoadingAvailability) {
      return <ActivityIndicator color={theme.loader.secondary} />;
    }
    if (!hasUrlChanges) return null;

    if (code.length > MAX_CODE_LENGTH) {
      return (
        <span style={styles.error}>{i18n.t(Translations.RequestGiftRouteMaxLengthError)}</span>
      );
    }

    if (availability?.checkGiftingCodeAvailability?.isAvailable) {
      return (
        <span style={styles.success}>{i18n.t(Translations.RequestGiftRouteLinkAvailable)}</span>
      );
    }

    return (
      <span style={styles.error}>{i18n.t(Translations.RequestGiftRouteLinkNotAvailable)}</span>
    );
  })();

  return (
    <MainLayout pageHeader={i18n.t(Translations.RequestGiftRouteHeader)}>
      <div style={styles.container}>
        <Spacing size={SpacingSizes.XL} />

        <H2>
          {isConfirmed
            ? i18n.t(Translations.RequestGiftRouteConfirmedTitle)
            : i18n.t(Translations.RequestGiftRouteInitialTitle)}
        </H2>
        <Spacing size={SpacingSizes.S} />

        <Body>
          {isConfirmed
            ? i18n.t(Translations.RequestGiftRouteConfirmedSubtitle, {
                name: beneficiaryName ?? "",
              })
            : i18n.t(Translations.RequestGiftRouteInitialSubtitle, {
                name: beneficiaryName ?? "",
              })}
        </Body>
        <Spacing size={SpacingSizes.L} />

        <div style={styles.linkRow}>
          <span style={styles.baseUrl}>{baseUrl}</span>
          <input
            style={styles.codeInput}
            value={code}
            onChange={(event) => onChangeCode(event.target.value)}
            readOnly={data.isGiftingCodeConfirmed}
            aria-label={i18n.t(Translations.RequestGiftRouteHeader)}
          />
          {isConfirmed && (
            <button
              type="button"
              style={styles.iconButton}
              onClick={handleCopy}
              aria-label={i18n.t(Translations.RequestGiftRouteCopy)}
            >
              {copied ? (
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="m4 12.5 5.5 5.5L20 6.5" />
                </svg>
              ) : (
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="9" y="9" width="12" height="12" rx="2" />
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                </svg>
              )}
            </button>
          )}
        </div>
        <Spacing size={SpacingSizes.S} />

        <div style={styles.message}>{validationMessage}</div>
        <Spacing size={SpacingSizes.XL} />

        <div style={styles.cta}>
          <Button
            title={primaryCTALabel}
            onClick={handleSubmit}
            loading={isPending}
            disabled={disabled}
          />
        </div>

        {isConfirmed && (
          <>
            <Spacing size={SpacingSizes.M} />
            <div style={styles.footer}>{i18n.t(Translations.RequestGiftRouteFooter)}</div>
          </>
        )}
        <Spacing size={SpacingSizes.XL} />
      </div>
    </MainLayout>
  );
};
